import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import PaymentsIcon from '@mui/icons-material/Payments';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CloseIcon from '@mui/icons-material/Close';

const Sidebar = ({ isOpen, toggleSidebar }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close sidebar on route change (mobile only)
    useEffect(() => {
        if (isMobile && isOpen) {
            toggleSidebar();
        }
    }, [location.pathname, isMobile]);

    const handleLogout = () => {
        localStorage.clear();
        navigate('/', { replace: true });
        if (isMobile && isOpen) {
            toggleSidebar();
        }
    };

    const handleLinkClick = () => {
        if (isMobile && isOpen) {
            toggleSidebar();
        }
    };

    const handleCloseClick = () => {
        if (isMobile && isOpen) {
            toggleSidebar();
        }
    };

    // Navigation items
    const navItems = [
        { path: '/overview', icon: <DashboardIcon />, label: 'Overview' },
        { path: '/financials', icon: <PaymentsIcon />, label: 'Financials' },
        { path: '/marketing', icon: <InsertChartIcon />, label: 'Marketing' },
        // { path: '/consultations', icon: <CalendarMonthIcon />, label: 'Consultations' },
        { path: '/patients', icon: <VaccinesIcon />, label: 'Patients' },
        { path: '/admin', icon: <ManageAccountsIcon />, label: 'Admins' },
    ];

    const isActive = (path) => location.pathname === path;

    // Determine if sidebar should be visible
    const isVisible = isMobile ? isOpen : true;

    return (
        <>
            <div className={`
                w-[280px] lg:w-[260px] lg:w-[280px]
                h-screen
                flex flex-col
                bg-white
                border-r border-gray-200
                shadow-lg lg:shadow-sm
                overflow-y-auto
                ${isMobile ? 'fixed top-0 left-0 z-50' : 'sticky top-0'}
                ${isMobile && !isVisible ? 'hidden' : 'block'}
            `}>
                {/* Close Button - Mobile Only */}
                {isMobile && (
                    <button
                        onClick={handleCloseClick}
                        className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        aria-label="Close sidebar"
                    >
                        <CloseIcon className="text-gray-600" />
                    </button>
                )}

                {/* Brand / Logo Section */}
                <div className="p-4 lg:p-5 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#069125] to-[#057a1f] flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-lg">QC</span>
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-gray-800 leading-tight">QuickCare</h1>
                            <p className="text-xs text-gray-400">Admin Panel</p>
                        </div>
                    </div>
                </div>

                {/* Navigation Links */}
                <div className="flex-1 overflow-y-auto p-3 space-y-1 mt-10 md:mt-0">
                    {navItems.map((item) => {
                        const active = isActive(item.path);
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={handleLinkClick}
                                className={`
                                    flex items-center gap-3 px-3 py-2.5 rounded-xl
                                    transition-all duration-200 group
                                    ${active 
                                        ? 'bg-gradient-to-r from-[#069125]/10 to-[#057a1f]/10 text-[#069125] font-semibold shadow-sm' 
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                                    }
                                `}
                            >
                                <span className={`
                                    ${active ? 'text-[#069125]' : 'text-gray-400 group-hover:text-gray-600'}
                                    transition-colors duration-200
                                `}>
                                    {item.icon}
                                </span>
                                <span className="text-sm font-medium flex-1">{item.label}</span>
                                {active && (
                                    <span className="w-1.5 h-6 rounded-full bg-[#069125]"></span>
                                )}
                            </Link>
                        );
                    })}
                </div>

                {/* Bottom Section - Settings & Logout */}
                <div className="p-3 border-t border-gray-100 space-y-1">
                    {/* Settings */}
                    <Link
                        to="/settings"
                        onClick={handleLinkClick}
                        className={`
                            flex items-center gap-3 px-3 py-2.5 rounded-xl
                            transition-all duration-200 group
                            ${isActive('/settings')
                                ? 'bg-gradient-to-r from-[#069125]/10 to-[#057a1f]/10 text-[#069125] font-semibold shadow-sm'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }
                        `}
                    >
                        <span className={`
                            ${isActive('/settings') ? 'text-[#069125]' : 'text-gray-400 group-hover:text-gray-600'}
                            transition-colors duration-200
                        `}>
                            <SettingsIcon />
                        </span>
                        <span className="text-sm font-medium flex-1">Settings</span>
                        {isActive('/settings') && (
                            <span className="w-1.5 h-6 rounded-full bg-[#069125]"></span>
                        )}
                    </Link>

                    {/* Logout */}
                    <button
                        onClick={handleLogout}
                        className="
                            w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                            text-red-600 hover:bg-red-50
                            transition-all duration-200 group
                        "
                    >
                        <span className="text-red-400 group-hover:text-red-600 transition-colors duration-200">
                            <LogoutIcon />
                        </span>
                        <span className="text-sm font-medium">Logout</span>
                    </button>

                    {/* Version Info */}
                    <div className="px-3 pt-3">
                        <p className="text-[10px] text-gray-400">Version 2.0.1</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;